// The Values form: a form whose SCHEMA is data.
//
// A section declares its editable slots in `types` — an array of /Types/Type,
// each with a list of params — and holds what was put in them in `values`.
// Nothing can describe `values` ahead of time, because its shape is whatever
// the author of that particular section decided. So the types array is COMPILED
// into a JSON Schema at render time and handed to the ordinary form component,
// which then behaves exactly as it does anywhere else: same widgets, same
// validation, same dirty tracking.
//
// That is the whole trick, and it is why a section can be edited by someone who
// will never see its markup.
//
// THE COMPILER IS NOT HERE ANY MORE.
//
// It is `@repoflow/types`, which the form-schema demo uses too. There was a
// copy in each place and they drifted in both directions: this one had a `time`
// param, date `format` and nullable numbers; the demo's had the termination
// rule for a recursive subform and the guard on the exclusive number bounds,
// both of which were live defects here. This one had no tests at all. What is
// left in this file is the React wrapper and the registry below.
import React from "react";
import _ from "lodash";
import {
  generateSchema as compile,
  defaultParams,
  paragraph,
  image,
  time,
  uid,
} from "@repoflow/types";
import { Component as FormSchema } from "@repoflow/form-schema";

/**
 * The param types this package compiles, which is the UNION of what the two
 * registries ask for: pagews declares `paragraph` and `image` and no `time`,
 * wfws declares `time` and neither of the others.
 *
 * That union is what the copy this replaced hardcoded, and it is why pagews
 * shipped wfws's branches and vice versa. It is kept here on purpose for now —
 * this change is meant to be behaviour-for-behaviour with what it replaces, and
 * narrowing it is a second change with a different blast radius. `Form` and
 * `generateSchema` both take a registry, so an app can narrow itself whenever
 * it wants to:
 *
 *   import { defaultParams, time } from "@repoflow/types";
 *   <Form types={types} params={{ ...defaultParams, time }} />
 *
 * The only thing the union costs is the error a mistake gets: a param type an
 * app has no widget for compiles here and then throws `widget.missing` from
 * inside the render, where a narrowed registry would have said
 * `types.param.unsupported:<name>` before rendering anything.
 */
export const params = {
  ...defaultParams,
  paragraph,
  image,
  time,
};

/**
 * Compile a types array. The second argument is the registry; the default is
 * the union above, which is what every caller got before this moved.
 */
export const generateSchema = (types, registry = params) =>
  compile(types, { params: registry, uid });

export const Form = (props) => {
  const {
    types,
    values,
    onChange,
    onSubmit,
    widgets,
    utils = [],
    readOnly = false,
    children,
    params: registry = params,
    // What to show instead of a form when there is nothing to compile one
    // from. The caller knows what "add a type" means on its screen — which tab,
    // and what the values are then for — and this component does not.
    empty = <span className="text-muted">No types defined.</span>,
  } = props;

  if (!types || types.length === 0) {
    return empty;
  }

  // Hard-coded everywhere — the renderer resolves a section's values against
  // "root", and so does this. A types array without it has no entry point.
  if (!_.find(types, { name: "root" })) {
    return <span className="text-muted">Type "root" not found.</span>;
  }

  let generated;
  try {
    generated = generateSchema(types, registry);
  } catch (e) {
    // A malformed types array is the author's own doing and is fixable on the
    // Types tab next door. Crashing the whole edit screen would take that tab
    // down with it, which is the one place the mistake can be undone.
    return <span className="text-danger">{e.toString()}</span>;
  }

  return (
    <FormSchema
      schema={[...utils, ...generated.schema]}
      config={generated.config}
      target={{ $ref: `/Type/root` }}
      widgets={widgets}
      values={values}
      onChange={onChange}
      onSubmit={onSubmit}
      readOnly={readOnly}
    >
      {children}
    </FormSchema>
  );
};
